/**
 * ACCESSIBLE-NAME SSOT for the whole product shell -- every `aria-label` an affordance answers to, wherever it is
 * rendered. Scope is the accessible NAME, not any one surface: the nav landmarks, the sidebar rail, the account menu,
 * the search box, the modal dismiss, the gallery controls, the admin list filters and the property tab strip all home
 * here. A screen-reader name that differs between two renderings of ONE affordance is a defect, and it is silent to
 * everyone who is not using a screen reader -- which is why the decision has one home rather than a literal per site.
 *
 * THIS HEADER STATES THE SCOPE BECAUSE A MEMBER'S DOCSTRING CANNOT: without it the first sentence a reader meets is
 * the founding member's own -- "the two controls that open the property gallery modal" -- which scopes the leaf to ONE
 * affordance on ONE surface while members spanning the sidebar, the account menu, the search box and three admin MFEs
 * sit under it, and a reader hunting a home for a nav landmark or an admin filter is told it is the wrong home.
 * {@link ARIA_LABEL_PRIMARY_MOBILE} and {@link ARIA_LABEL_FILTER_OFFERS_BY_STATUS} are that class: two shells each,
 * neither a gallery control.
 *
 * `ARIA_LABEL_*` RATHER THAN THE `LABEL_*` PREFIX of `const/label-values.ts`, and the distinction is the ACCESSIBLE
 * NAME versus the VISIBLE label -- a control may carry one of each, with different text, so folding the two prefixes
 * would assert an identity that does not hold.
 *
 * WHICH MATCHER GUARDS THESE MEMBERS, re-measured 2026-09-05 through the PRODUCTION opts bag against the live const
 * tree, because the paragraph that stood here until that date named the wrong mechanism and prescribed a repair that
 * had already landed. It said `QUAL-JSX-ATTR-CONST-ADOPTION-001`'s attribute regex "admits no hyphen", so
 * `aria-label="X"` was read as the bare attribute `label` and looked up LABEL_X, and that the cure was to admit `-`.
 * FALSIFIED by the discriminating probe that prose implies: `label="Back"` fires 1 row prescribing `LABEL_BACK` (the
 * positive control, so the harness is reading the real const index), while `aria-label="Back"` fires ZERO -- which it
 * could not do if the name were being read as bare `label`. `JSX_STRING_ATTR_RE` admits the hyphen at HEAD and the
 * true name reaches the arm; `aria-*` is then refused ON PURPOSE by `isJsxLintAnchoredAttr`, whose stated ground is
 * that a LINTER reads `aria-*` and prescribing a const there is the measured `rel="noopener noreferrer"` security
 * regression class. So this leaf's members are permanently outside that rule BY DESIGN, and nothing is owed at it.
 *
 * THE GUARD IS `QUAL-INLINE-UI-COPY-LITERAL-001` (ACTIVE), which namespace-imports this leaf and feeds every STRING
 * export into `FORBIDDEN_COPY` via `addStringExportOwners`. Measured the same day: inline `'Open gallery'`,
 * `'Close modal'` and `'Property details sections'` each fire exactly 1 row naming this leaf's owning export, and an
 * unowned literal fires 0.
 *
 * THE FIFTEEN FLAT EXPORTS ARE A `QUAL-CONST-GROUP-001` ROW AND THE GROUPING IS DECLINED FOR NOW, recorded here so the
 * next sweep does not re-derive it. `addStringExportOwners` skips any export whose value is not a string
 * (`typeof value !== 'string'`), so folding the family into one grouped object drops all fifteen accessible names out
 * of `FORBIDDEN_COPY` and takes that ACTIVE rule DARK over the whole accessible-name class -- silently, since a rule
 * with fewer owned values still fires on the ones it kept. The fold is therefore PAIRED, not foreclosed: the sibling
 * helper `addRecordMemberOwners` (`utils/compliance/rule-helpers/forbidden-literal-owner-scan.ts`) exists for exactly
 * this shape, so the grouping must land in ONE change with that rule's composition switched to it. FALSIFIER: this
 * decline is void the moment `QUAL-INLINE-UI-COPY-LITERAL-001` reads this leaf through `addRecordMemberOwners` (or any
 * record-shaped owner walk) -- re-run the three-value probe above against the grouped spelling, and if it still
 * reports 3 rows the grouping is owed.
 */

/** The screen-reader name of the two controls that open the property gallery modal: the mobile carousel slide button
 *  (`PropertyGallery.components.tsx`) and the desktop hero tile (`PropertyGallery.layouts.tsx`). One affordance, two
 *  breakpoints -- its siblings `ARIA_LABEL_NEXT_IMAGE` / `ARIA_LABEL_PREVIOUS_IMAGE` were already homed here. */
export const ARIA_LABEL_OPEN_GALLERY = 'Open gallery';
/**
 * Accessible name for the home affordance. Spelled at TWO surfaces that never see each other -- the root error page's
 * secondary action (`apps/federa/src/app/error.tsx`) and the sidebar brand link (`components/layout/SidebarParts.tsx`)
 * -- and a screen-reader name that differs between the two ways out of a page is a defect, not a variant.
 */
export const ARIA_LABEL_GO_TO_HOME_PAGE = 'Go to home page';

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the alt text for one property photo, cover or not.
 *
 * A FUNCTION rather than two exported strings because the DECISION is the ternary, not the words: the cover flag
 * selecting the alt text was hand-spelled identically in `components/navigator/steps/PhotosThumbnail.tsx` and
 * `steps/ReviewStepSections.sections.tsx`, and two copies of a branch are two places for a screen-reader name to
 * diverge -- the failure being silent to everyone who is not using one.
 *
 * The parameter admits `undefined` because BOTH consumers read it off `NavigatorPhotoEntry.isCover`, which is
 * optional: an absent flag IS "not the cover", so widening here is the honest contract rather than a coercion
 * hand-spelled at each call site (which is the duplication this helper exists to prevent).
 */
export function propertyPhotoAlt(isCover: boolean | undefined): string {
	return isCover ? 'Cover property photo' : 'Property photo';
}

/**
 * The sidebar collapse/expand pair. Spelled at THREE surfaces that never see each other -- the shared sidebar rail
 * (`components/layout/SidebarParts.tsx`), the sidebar's own nav landmark while collapsed
 * (`components/layout/AppSidebar.tsx`) and the profile SPA's private sidebar (`spa/profile/frontend/main-sidebar.tsx`)
 * -- and one control answering to two names depending on which shell rendered it is a defect that is silent to
 * everyone not using a screen reader.
 */
export const ARIA_LABEL_COLLAPSE_SIDEBAR = 'Collapse sidebar';

export const ARIA_LABEL_EXPAND_SIDEBAR = 'Expand sidebar';

/** The account/avatar menu affordance, named by the shared shell's menu button and again by the profile SPA's own dropdown. */
export const ARIA_LABEL_USER_MENU = 'User menu';

/** The clear-the-search-box control: an inline addon on the shared toolbar and the explore search, and a ghost button in the mobile sheet. One affordance, three renderings. */
export const ARIA_LABEL_CLEAR_SEARCH = 'Clear search';

/** The modal dismiss control, named by the `Modal` primitive's own close button and by the property modal's hand-placed one. */
export const ARIA_LABEL_CLOSE_MODAL = 'Close modal';

/** The share-this-property affordance, on the property card and again on the feed's overlay row. */
export const ARIA_LABEL_SHARE_PROPERTY = 'Share property';

export const ARIA_LABEL_NEXT_IMAGE = 'Next image';

export const ARIA_LABEL_PREVIOUS_IMAGE = 'Previous image';

/**
 * Names the status-filter control on the admin list surfaces. Spelled at three MFEs that never see each other --
 * `admin-actions`' and `admin-cicd`'s `FilterSelect` and `admin-finance`'s payouts `Select` -- and they are the same
 * affordance over the same column, so a screen-reader name that differs between them is a defect.
 *
 * DELIBERATELY NOT `STATUS_FILTER_INPUT_DESCRIPTION` (`const/flow-schema-descriptions.ts`): that one documents a FLOW
 * INPUT FIELD for schema consumers and is free to grow API detail this label must never speak. The two say different
 * words on purpose: the same words would be a QUAL-CROSS-FILE-PHRASE-CONST-DUP-001 conviction, and adopting this label
 * there is what the paragraph above forbids.
 */
export const ARIA_LABEL_FILTER_BY_STATUS = 'Filter by status';

/** Names the offers sort control, rendered at both breakpoints of `mfe/offers`' filter-sort bar -- one affordance, two
 *  layouts, the same shape as `ARIA_LABEL_OPEN_GALLERY` above. */
export const ARIA_LABEL_SORT_OFFERS = 'Sort offers';

/**
 * The MOBILE NAV LANDMARK's accessible name, spelled at two shells that never see each other -- the host app's bottom
 * bar (`apps/federa/src/components/layout/MobileBottomNav.tsx`) and the profile SPA's own
 * (`spa/profile/frontend/main-mobile-nav.tsx`). Both mark a `role="navigation"` region as the primary mobile nav, and
 * a landmark answering to two names depending on which shell rendered it is the same defect the sidebar pair above
 * records: it is announced to exactly the users who cannot see that the two bars are the same thing.
 */
export const ARIA_LABEL_PRIMARY_MOBILE = 'Primary mobile';

/**
 * Names the OFFER status filter -- the offers-scoped sibling of {@link ARIA_LABEL_FILTER_BY_STATUS}. Spelled at two
 * surfaces that never see each other, `mfe/admin-users`' user-detail offers table and `spa/market-maker`'s deal
 * sections, both naming a `Select` over the same offer-status column; each site's own comment records the same finding
 * independently (an unnamed combo box announced only its current value), which is the two-transcriptions-of-one-
 * decision shape rather than two decisions.
 *
 * DELIBERATELY NOT FOLDED INTO {@link ARIA_LABEL_FILTER_BY_STATUS}, whose value is the shorter `Filter by status`:
 * that one names a filter over the ADMIN list surfaces' own status column, and these two sit beside OTHER filters in a
 * bar where "Filter by status" would not say which column. Two different accessible names because two different
 * controls, which is the boundary that member's own docstring already draws against the flow-schema description.
 */
export const ARIA_LABEL_FILTER_OFFERS_BY_STATUS = 'Filter offers by status';

/** Names the property-detail `tablist`. Three surfaces render the same tab strip over the same sections -- the public
 *  `PropertyDetailsTabs`, the seller's `PropertyDetailView.about` sub-tab strip and the transaction
 *  `AboutPropertySection` -- across two packages, which is exactly where a hand-spelled name drifts. */
export const ARIA_LABEL_PROPERTY_DETAILS_SECTIONS = 'Property details sections';

/** Names the profile surface's `tablist` (`spa/profile/frontend/ProfileContent.tsx`), the sibling of
 *  {@link ARIA_LABEL_PROPERTY_DETAILS_SECTIONS}: the embedded tab strip over the person, their contacts and their
 *  balances. A literal at the one call site was the last hand-spelled accessible name on that surface. Flat like its
 *  fifteen siblings: the `const-add` flow asks for a grouped `ARIA` object, and one grouped key beside fifteen flat
 *  exports would be a second spelling of this leaf's shape. */
export const ARIA_LABEL_PROFILE_SECTIONS = 'Profile sections';
